/*
  Lavish AOV, 2026-10-18. One-click add and bundle swap for the Lavish cart offers and the
  product-page bundle upgrade. Each action is announced exactly the way Vessel's own product
  form announces an add (a CartLinesUpdateEvent from @shopify/events, resolved with the fresh
  cart and the re-rendered drawer sections), so the drawer, the cart count and every app that
  listens to cart events stay in step.

  Markup:
    <button data-lavish-add data-variant-id="123" data-quantity="1">Add</button>
    <button data-lavish-add data-variant-id="123" data-selling-plan="789">Subscribe and save</button>  (23 Oct: on a plan, the quiz)
    <button data-lavish-add-items='[{"id": 123, "quantity": 1}, {"id": 456, "quantity": 1}]'>Add both</button>
    <button data-lavish-swap data-variant-id="456" data-remove='{"lineKey": 0}'>Swap</button>
    <button data-lavish-plan="789" data-line="1" data-quantity="1">switch</button>  (an empty plan buys once)
*/
import { CartLinesUpdateEvent } from '@shopify/events';

const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };

const drawerSectionIds = () =>
  [...document.querySelectorAll('cart-items-component')]
    .map((element) => element.dataset.sectionId)
    .filter(Boolean)
    .join(',');

async function freshCart() {
  const component = document.querySelector('cart-items-component');
  if (component) {
    await customElements.whenDefined('cart-items-component');
    return component.fetchCartData();
  }
  return fetch(`${Theme.routes.cart_url}.json`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
}

async function addLines(source, lines, before) {
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: lines.map((line) => ({ merchandiseId: String(line.id), quantity: line.quantity })),
      promise: deferred.promise,
    })
  );
  try {
    if (before) await before();
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({
        items: lines.map((line) => ({
          id: Number(line.id),
          quantity: line.quantity,
          ...(line.selling_plan ? { selling_plan: Number(line.selling_plan) } : {}),
        })),
        sections: drawerSectionIds(),
      }),
    }).then((r) => r.json());
    const cart = await freshCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'lavish-aov',
        sourceId: source.id || 'lavish-aov',
        itemCount: lines.reduce((sum, line) => sum + line.quantity, 0),
        sections: response.sections,
        didError: Boolean(response.status),
      },
    });
    return !response.status;
  } catch (error) {
    deferred.reject(error);
    return false;
  }
}

/*
  Subscribe and save in the cart, 22 Oct (snippets/lavish-cart-subscribe.liquid): the same line moves onto its plan,
  or back to buying once. The Cart API changes a line's plan only by its position (`line`), with its quantity; an
  empty plan is sent as null, which takes the plan off. The drawer re-renders from the returned sections.
*/
async function changeLinePlan(source, { line, quantity, plan }) {
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'update',
      context: 'cart',
      lines: [{ id: source.closest('[data-key]')?.dataset.key ?? '', quantity }],
      promise: deferred.promise,
    })
  );
  try {
    const response = await fetch(`${Theme.routes.cart_change_url}.js`, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ line, quantity, selling_plan: plan ? Number(plan) : null, sections: drawerSectionIds() }),
    }).then((r) => r.json());
    if (response.status) throw new Error(response.description || response.message || 'Cart update failed');
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(response),
      detail: {
        items: response.items,
        source: 'lavish-aov',
        sourceId: source.id || 'lavish-aov',
        itemCount: response.item_count,
        sections: response.sections,
        didError: false,
      },
    });
    return true;
  } catch (error) {
    deferred.reject(error);
    return false;
  }
}

document.addEventListener('click', async (event) => {
  const button = event.target instanceof Element
    ? event.target.closest('[data-lavish-add], [data-lavish-add-items], [data-lavish-swap], [data-lavish-plan]')
    : null;
  if (!button || button.disabled) return;
  event.preventDefault();
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');

  if (button.hasAttribute('data-lavish-plan')) {
    const ok = await changeLinePlan(button, {
      line: Number(button.dataset.line),
      quantity: Number(button.dataset.quantity || 1),
      plan: button.dataset.lavishPlan,
    });
    // On success the row is re-rendered with its new plan; only a failure leaves this button to restore.
    if (!ok) {
      button.removeAttribute('aria-busy');
      button.disabled = false;
    }
    return;
  }

  const line = button.hasAttribute('data-lavish-add-items')
    ? JSON.parse(button.dataset.lavishAddItems)
    : [{ id: button.dataset.variantId, quantity: Number(button.dataset.quantity || 1), selling_plan: button.dataset.sellingPlan }];
  let ok;
  if (button.hasAttribute('data-lavish-swap')) {
    const updates = JSON.parse(button.dataset.remove || '{}');
    ok = await addLines(button, line, () =>
      fetch(`${Theme.routes.cart_update_url}.js`, { method: 'POST', headers: jsonHeaders, body: JSON.stringify({ updates }) })
    );
  } else {
    ok = await addLines(button, line);
  }

  button.removeAttribute('aria-busy');
  if (ok) {
    button.textContent = button.dataset.doneLabel || 'Added';
  } else {
    button.disabled = false;
  }
});
